import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking");
  const sessionId = params.get("session_id");

  useEffect(() => {
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch {
        setState("failed");
        return;
      }
      if (tries >= 8) {
        setState("pending");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    if (sessionId) poll();
    else setState("failed");
    return () => clearTimeout(timer);
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen grid place-items-center px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full border border-border p-8 text-center fade-up">
        {state === "checking" && (
          <>
            <Loader2 className="h-6 w-6 mx-auto animate-spin text-primary" />
            <p className="mt-5 font-display text-xl font-bold tracking-tight">Confirming your payment…</p>
            <p className="mt-2 text-sm text-muted-foreground">This usually takes a couple of seconds.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="h-6 w-6 mx-auto text-[hsl(var(--success))]" />
            <p className="mt-5 font-display text-xl font-bold tracking-tight">Plan activated</p>
            <p className="mt-2 text-sm text-muted-foreground">Labs, capstone reviews and mock interviews are unlocked.</p>
            <Link to="/dashboard" className="inline-block mt-6" data-testid="payment-success-dashboard">
              <Button className="rounded-none">Go to dashboard</Button>
            </Link>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="h-6 w-6 mx-auto text-[hsl(var(--warning))]" />
            <p className="mt-5 font-display text-xl font-bold tracking-tight">Still processing</p>
            <p className="mt-2 text-sm text-muted-foreground">Your payment has not confirmed yet. Refresh this page in a minute.</p>
            <Link to="/profile" className="inline-block mt-6"><Button variant="outline" className="rounded-none">Check my plan</Button></Link>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="h-6 w-6 mx-auto text-destructive" />
            <p className="mt-5 font-display text-xl font-bold tracking-tight">We could not confirm that payment</p>
            <p className="mt-2 text-sm text-muted-foreground">No charge was applied if the session did not complete.</p>
            <Link to="/pricing" className="inline-block mt-6"><Button className="rounded-none">Back to pricing</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen grid place-items-center px-6" data-testid="payment-cancel-page">
      <div className="max-w-md w-full border border-border p-8 text-center">
        <XCircle className="h-6 w-6 mx-auto text-muted-foreground" />
        <p className="mt-5 font-display text-xl font-bold tracking-tight">Checkout cancelled</p>
        <p className="mt-2 text-sm text-muted-foreground">Nothing was charged. You can upgrade whenever you are ready.</p>
        <Button className="mt-6 rounded-none" onClick={() => navigate("/pricing")} data-testid="payment-cancel-back">
          Back to pricing
        </Button>
      </div>
    </div>
  );
}
